import { differenceInDays } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Subscription } from "@/types/subscription";

type Props = {
  subscription: Subscription | null;
  /**
   * Planning concerné, quand la salle en utilise plusieurs. Sans lui, un
   * adhérent détenant deux abonnements verrait deux alertes identiques sans
   * savoir laquelle vise quel planning. `null` = salle sans multi-planning :
   * le libellé n'est pas affiché.
   */
  calendarLabel?: string | null;
  featured?: boolean;
};

export function SubscriptionStatusAlert({ subscription, calendarLabel = null, featured = false }: Props) {
  const { t } = useTranslation(["subscriptions"]);

  if (!subscription) return null;

  const daysLeft = differenceInDays(new Date(subscription.end_date), new Date());
  const isExpiringSoon = daysLeft <= 30 && daysLeft >= 0;
  const isExpired = daysLeft < 0;
  const sessionsRemaining = subscription.sessions_remaining ?? 0;

  if (!(isExpired || isExpiringSoon || sessionsRemaining <= 3)) return null;

  const variant =
    isExpired || sessionsRemaining === 0 ? "destructive" : "warning";

  const title = isExpired
    ? t("expiredAlertTitle")
    : sessionsRemaining === 0
    ? t("noSessionsTitle")
    : sessionsRemaining <= 3
    ? t("lowSessionsTitle")
    : t("expiringSoonTitle");

  const description = isExpired
    ? t("expiredAlertDescription")
    : sessionsRemaining === 0
    ? t("noSessionsDescription")
    : sessionsRemaining <= 3
    ? t("lowSessionsDescription", { remaining: sessionsRemaining })
    : t("expiringSoonDescription", { daysLeft });

  return (
    <Alert variant={variant as any} className={featured ? "mb-4 rounded-md border-primary/45 bg-card/90 backdrop-blur-sm" : "mb-4 backdrop-blur-sm bg-card/60 border-border/40"}>
      <AlertTriangle className="h-5 w-5" />
      <AlertTitle>
        {title}
        {calendarLabel && (
          <span className="ml-2 text-xs font-normal opacity-80">— {calendarLabel}</span>
        )}
      </AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  );
}
